import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon, Info } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

import { useShop } from '@/hooks/useShop';
import { useGoBack } from '@/hooks/useGoBack';
import { useStockRecord, editProduct, setProductChannels } from '@/hooks/useInventory';
import { getTitle } from '@/lib/channels';
import { hideProducts } from '@services/stock';
import { saveFileToS3 } from '@services/shop';

import { CategoryPicker } from './components/CategoryPicker';
import { DeliveryChannelsCard } from './components/DeliveryChannelsCard';
import { VisibilityCard } from './components/VisibilityCard';
import { IssuesCard } from './components/IssuesCard';
import { FixIssueDialog } from './components/FixIssueDialog';
import { FIXABLE_ISSUES, sortIssueTypes } from './lib/fix-copy';
import { SELF_SOURCING_CHANNELS, TITLE_FLOOR_CHANNELS, resolveIssuesChannelId, computeChannelVisibility } from '../lib/channel-visibility';
import { stashReturnBarcode } from '../lib/return-to-row';

function Field({ label, required, children }) {
	return (
		<div>
			<div className="mb-1.5 text-sm font-semibold text-foreground">
				{label} {required ? <span className="text-destructive">*</span> : null}
			</div>
			{children}
		</div>
	);
}

function StockRow({ label, value, mono }) {
	return (
		<div className="flex items-center justify-between py-1.5 text-sm">
			<span className="text-muted-foreground">{label}</span>
			<span className={`font-semibold text-foreground ${mono ? 'tabular-nums' : ''}`}>{value}</span>
		</div>
	);
}

const INPUT_CLASS =
	'w-full h-11 px-3.5 rounded-lg border border-border bg-white text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';

// Given a channel and the record's issues, return every issue type that blocks
// that channel AND is fixable from this page, in display order. Self-sourcing
// channels never block; storefront blocks only on missing title; others block
// on any issue.
function findFixableIssues(channelId, record) {
	if (!record) return [];
	const id = resolveIssuesChannelId(channelId);
	if (SELF_SOURCING_CHANNELS.has(id)) return [];
	const issues = record.issues || [];
	if (TITLE_FLOOR_CHANNELS.has(id)) {
		return issues.includes('MissingProductTitle') ? ['MissingProductTitle'] : [];
	}
	return sortIssueTypes(issues.filter((i) => FIXABLE_ISSUES[i]));
}

export function ProductDetail() {
	const { barcode = '' } = useParams();
	const { shop } = useShop();
	const goBack = useGoBack('/inventory/manage');
	const { record, isLoading, mutate } = useStockRecord(shop?.id, barcode);

	// Every route back to the list (Back / Save / Cancel) stashes this barcode
	// so the remounted list scrolls the row back into view (NEA-6342).
	const returnToList = () => {
		stashReturnBarcode(barcode);
		goBack();
	};

	// The shop resolves asynchronously; until its id is known the record query
	// can't even start (the SWR key is null, so `isLoading` stays false).
	// Treat that window as loading too, otherwise the "not found" card flashes
	// for a product that actually exists.
	const resolvingRecord = !shop?.id || isLoading;

	const [form, setForm] = useState({ title: '', brand: '', description: '', category: null });
	const [imageSrc, setImageSrc] = useState('');
	const [imageFile, setImageFile] = useState(null);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(null);
	const [togglingActive, setTogglingActive] = useState(false);
	const [channelsOpen, setChannelsOpen] = useState(false);
	const [channelDraft, setChannelDraft] = useState({});
	const [savingChannels, setSavingChannels] = useState(false);
	// Fix dialog state — `{ type, requiredBy, alsoNeeds }` while open, null while
	// closed. Opened either from an Issues card row (subject = the issue, so
	// `requiredBy` is every channel it blocks) or from a Visibility row's Fix
	// button (subject = the channel, so `alsoNeeds` carries that channel's
	// remaining issues).
	const [fixIssue, setFixIssue] = useState(null);

	// Refs for the form controls the Fix dialog's action jumps to. Text fields
	// are scrolled + focused + briefly ring-highlighted; the image ref is the
	// hidden file input (we click it to open the OS picker); the category calls
	// CategoryPicker's imperative open() to pop the taxonomy dialog directly,
	// since scrolling to the trigger is pointless when a dialog covers the page.
	const titleRef = useRef(null);
	const brandRef = useRef(null);
	const descriptionRef = useRef(null);
	const categoryOpenRef = useRef(null);
	const imageInputRef = useRef(null);

	const applyFix = (field) => {
		setFixIssue(null);
		if (!field) return;
		if (field === 'category') {
			categoryOpenRef.current?.();
			return;
		}
		if (field === 'image') {
			imageInputRef.current?.click();
			return;
		}
		const node = { title: titleRef, brand: brandRef, description: descriptionRef }[field]?.current;
		if (!node) return;
		node.scrollIntoView({ behavior: 'smooth', block: 'center' });
		// Focus after the scroll starts so the caret jump doesn't fight the
		// scroll, and flash a ring so the retailer sees which field to edit.
		setTimeout(() => node.focus?.({ preventScroll: true }), 100);
		node.classList.add('ring-2', 'ring-primary');
		setTimeout(() => node.classList.remove('ring-2', 'ring-primary'), 1600);
	};

	// Channels editable per-product: those the shop has configured for this
	// item (a key on `record.channels`) AND that support product-specific
	// visibility — same shape the retailers dashboard's
	// EditChannelVisibilityModal uses.
	// `availableChannels` labels come straight from `@nearst/channel-definitions`,
	// which is NearSt-voiced ("Storefront", "Product Reservations") — pass them
	// through the Unify presentation override so this page names channels the way
	// the rest of the dashboard does (NEA-6104).
	const channelLabel = (id) => getTitle(id, { title: shop?.availableChannels?.[id]?.label ?? id.replace(/-/g, ' ') });
	const editableChannels = Object.keys(record?.channels ?? {})
		.filter((id) => shop?.availableChannels?.[id]?.productSpecificVisibility)
		.map((id) => ({ id, label: channelLabel(id) }));

	useEffect(() => {
		if (!record) return;
		// productCategory can arrive as a string (legacy "Pet Food") or an
		// object ({ id, title, full_tree }). Normalise into the picker's shape.
		const initialCategory = record.productCategory
			? typeof record.productCategory === 'object'
				? { id: record.productCategory.id, title: record.productCategory.title, fullTree: record.productCategory.full_tree }
				: { id: null, title: record.productCategory, fullTree: null }
			: null;
		setForm({
			title: record.title ?? '',
			brand: record.brand ?? '',
			description: record.description ?? '',
			category: initialCategory
		});
		setImageSrc(record.images?.[0] ?? '');
		setImageFile(null);
	}, [record]);

	const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

	const onPickImage = (e) => {
		const file = e.target.files?.[0];
		if (!file?.name) return;
		setImageFile(file);
		setImageSrc(URL.createObjectURL(file));
	};

	const onSave = async () => {
		if (!shop?.id) return;
		setSaving(true);
		setSaveError(null);
		try {
			// Only a freshly-picked file needs uploading; an unchanged image
			// keeps the record's existing URL.
			let images = record?.images ?? [];
			if (imageFile) {
				const url = await saveFileToS3(shop.id, imageFile);
				images = [url, ...images.filter((u) => u !== url)];
			}
			await editProduct(shop.id, barcode, {
				title: form.title,
				brand: form.brand,
				description: form.description,
				images,
				// Sending categoryIds matches the retailers-dashboard write path.
				// Empty array clears the override. A picked-but-no-id entry (legacy
				// string-only data) is sent as the `category` title instead.
				...(form.category?.id ? { categoryIds: [form.category.id] } : {}),
				...(form.category && !form.category.id ? { category: form.category.title } : {}),
				...(form.category === null ? { categoryIds: [], category: '' } : {})
			});
			await mutate();
			setImageFile(null);
			// Return to the product overview so the save is visibly acknowledged
			// instead of silently staying put (NEA-5977).
			returnToList();
		} catch (e) {
			setSaveError(e?.response?.data?.message ?? e.message ?? 'Something went wrong saving this product.');
		} finally {
			setSaving(false);
		}
	};

	const onToggleActive = async () => {
		if (!shop?.id || !record) return;
		setTogglingActive(true);
		try {
			await hideProducts(shop.id, [barcode], record.hidden);
			await mutate();
		} finally {
			setTogglingActive(false);
		}
	};

	// Every channel configured on this item, alphabetised — the tri-state
	// icon carries the affordance so we don't need to hoist blocked/hidden
	// rows out of alphabetical order (matches the design).
	const productHidden = !!record?.hidden;
	const channelStates = record?.channels
		? Object.entries(record.channels)
				.map(([id, on]) => ({
					id,
					label: channelLabel(id),
					state: computeChannelVisibility(id, !!on, record),
					fixableIssues: findFixableIssues(id, record)
				}))
				.sort((a, b) => a.label.localeCompare(b.label))
		: [];

	// Issues card feed — one row per fixable issue that still blocks at least
	// one of this product's channels. Non-fixable issues (InvalidCondition,
	// MissingDeliveryCategories) are excluded because there's nowhere on this
	// page to resolve them. Deriving the affected channels from each channel's
	// own fixable set inherits the Visibility card's per-channel gating
	// (self-sourcing / title-floor), so an issue that no longer blocks anything
	// drops off rather than lingering as an un-actionable row. The `blocked`
	// guard matters as much as the issue match: a channel the retailer toggled
	// OFF for this product — or every channel, when the whole product is hidden
	// — isn't waiting on a fix, and counting it would promise an unlock that
	// editing the field can't deliver. With nothing blocked the outer filter
	// empties the list and the card disappears entirely.
	const fixableIssues = sortIssueTypes((record?.issues ?? []).filter((type) => FIXABLE_ISSUES[type]))
		.map((type) => ({
			type,
			label: FIXABLE_ISSUES[type].label,
			affectedChannels: channelStates
				.filter(({ state, fixableIssues: types }) => state === 'blocked' && types.includes(type))
				.map(({ label }) => label)
		}))
		.filter((issue) => issue.affectedChannels.length > 0);

	const openChannelEditor = () => {
		const current = record?.channels ?? {};
		setChannelDraft(Object.fromEntries(editableChannels.map((c) => [c.id, current[c.id] !== false])));
		setChannelsOpen(true);
	};

	const saveChannels = async () => {
		if (!shop?.id || !record) return;
		setSavingChannels(true);
		try {
			await setProductChannels(shop.id, [barcode], { ...(record.channels ?? {}), ...channelDraft });
			await mutate();
			setChannelsOpen(false);
		} finally {
			setSavingChannels(false);
		}
	};

	return (
		<div className="space-y-6">
			<button
				type="button"
				onClick={returnToList}
				className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
			>
				<ArrowLeft className="h-3 w-3" /> Back
			</button>

			<header>
				<h1 className="text-3xl font-extrabold tracking-tight tabular-nums md:text-[32px]">{barcode}</h1>
				<p className="mt-1.5 text-muted-foreground">Edit product information and configure visibility across enabled channels.</p>
			</header>

			{resolvingRecord ? (
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
					<Skeleton className="h-[460px] w-full rounded-2xl" />
					<Skeleton className="h-[320px] w-full rounded-2xl" />
				</div>
			) : !record ? (
				<SoftCard className="!p-8 text-center">
					<p className="text-sm text-muted-foreground">
						We couldn't find a product with barcode <span className="font-semibold text-foreground">{barcode}</span> in your
						catalogue.
					</p>
				</SoftCard>
			) : (
				// items-start stops the two columns stretching to a shared row
				// height — without it, expanding a sidebar card pads dead space
				// into the Product data card below its Save button.
				<div data-testid="product-detail-grid" className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_320px]">
					<SoftCard className="!p-6">
						<div className="mb-5 flex items-center justify-between">
							<h2 className="text-base font-bold text-foreground">Product data</h2>
							<TooltipProvider delayDuration={150}>
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="inline-flex cursor-help items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
											Third party data
											<Info className="h-3 w-3" />
										</span>
									</TooltipTrigger>
									<TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
										This product information is sourced from publicly available data. We recommend reviewing it for
										accuracy and refining any details to best represent your product.
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</div>

						<div className="grid grid-cols-1 gap-5 md:grid-cols-[1fr_180px]">
							<div className="space-y-4">
								<Field label="Title" required>
									<input
										ref={titleRef}
										type="text"
										value={form.title}
										onChange={setField('title')}
										className={INPUT_CLASS}
									/>
								</Field>
								<Field label="Brand">
									<input
										ref={brandRef}
										type="text"
										value={form.brand}
										onChange={setField('brand')}
										className={INPUT_CLASS}
									/>
								</Field>
								<Field label="Product category">
									<CategoryPicker
										value={form.category}
										onChange={(c) => setForm((f) => ({ ...f, category: c }))}
										openRef={categoryOpenRef}
									/>
								</Field>
							</div>

							<Field label="Image">
								<label className="block cursor-pointer">
									<div className="flex h-[180px] items-center justify-center overflow-hidden rounded-lg border border-border bg-white p-2 transition hover:border-primary">
										{imageSrc ? (
											<img src={imageSrc} alt={form.title} className="max-h-full max-w-full object-contain" />
										) : (
											<ImageIcon className="h-10 w-10 text-muted-foreground" />
										)}
									</div>
									<span className="mt-2 block w-full text-center text-sm font-semibold text-primary hover:underline">
										{imageSrc ? 'Edit image' : 'Upload image'}
									</span>
									<input ref={imageInputRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" />
								</label>
							</Field>
						</div>

						<div className="mt-5">
							<Field label="Description">
								<textarea
									ref={descriptionRef}
									rows={6}
									value={form.description}
									onChange={setField('description')}
									className="w-full resize-y rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm leading-relaxed outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
								/>
							</Field>
						</div>

						<div className="mt-5 flex items-center gap-3">
							<button
								type="button"
								onClick={onSave}
								disabled={saving}
								className="inline-flex items-center rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
							>
								{saving ? 'Saving…' : 'Save'}
							</button>
							{/* Pops back like Save does (rather than pushing a fresh list
							    entry) so the list's page / search / filters survive. */}
							<button
								type="button"
								onClick={returnToList}
								className="inline-flex items-center rounded-full border border-border bg-white px-5 py-2 text-sm font-semibold text-foreground transition hover:bg-accent"
							>
								Cancel
							</button>
						</div>
						{saveError ? <p className="mt-3 text-sm text-destructive">{saveError}</p> : null}
					</SoftCard>

					<div className="space-y-6">
						<SoftCard className="!p-5">
							<h3 className="mb-3 text-base font-bold">Stock data</h3>
							<StockRow label="Barcode" value={barcode} mono />
							<StockRow label="Price" value={record.price != null ? `£${Number(record.price).toFixed(2)}` : '—'} />
							<StockRow label="Quantity" value={record.quantity != null ? `${record.quantity} in stock` : '—'} />
						</SoftCard>

						<VisibilityCard
							channelStates={channelStates}
							hidden={productHidden}
							togglingActive={togglingActive}
							onToggleActive={onToggleActive}
							canEditChannels={editableChannels.length > 0}
							onEditChannels={openChannelEditor}
							onFix={(channelId, types) =>
								setFixIssue({
									type: types[0],
									requiredBy: [channelLabel(channelId)],
									alsoNeeds: types.slice(1)
								})
							}
						/>

						<IssuesCard
							issues={fixableIssues}
							onFix={(issue) => setFixIssue({ type: issue.type, requiredBy: issue.affectedChannels })}
						/>

						<DeliveryChannelsCard record={record} shopId={shop?.id} onSaved={mutate} />
					</div>
				</div>
			)}

			<FixIssueDialog issue={fixIssue} onApply={applyFix} onClose={() => setFixIssue(null)} />

			<Dialog open={channelsOpen} onOpenChange={setChannelsOpen}>
				<DialogContent className="rounded-2xl sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="text-xl font-bold">Edit channel visibility</DialogTitle>
						<DialogDescription>Select the channels where you want to display this product.</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="flex flex-col gap-3">
							{editableChannels.map((c) => (
								<Checkbox
									key={c.id}
									label={c.label}
									checked={!!channelDraft[c.id]}
									onChange={(e) => setChannelDraft((d) => ({ ...d, [c.id]: e.target.checked }))}
								/>
							))}
						</div>
						<button
							type="button"
							onClick={saveChannels}
							disabled={savingChannels}
							className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
						>
							{savingChannels ? 'Saving…' : 'Confirm'}
						</button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
